import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  InputNumber,
  message,
  Popconfirm,
  Space,
  Tag,
  Tabs
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined
} from '@ant-design/icons';
import axios from 'axios';
import API_ENDPOINTS from '../config/api';

// Create axios instance like adminApi (using relative URL for proxy)
const suppliersApi = axios.create({
  baseURL: '/api',
  timeout: 30000,
});

// Request interceptor to add JWT token (like adminApi)
suppliersApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['Content-Type'] = 'application/json';
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const { Option } = Select;
const { TabPane } = Tabs;

const SupplierManagementSimple = () => {
  const [mappings, setMappings] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [masterProducts, setMasterProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('suppliers');
  const [searchText, setSearchText] = useState('');
  const [filteredMappings, setFilteredMappings] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0); // Add refresh key to force re-render
  const switchRef = useRef(null);
  const [switchValue, setSwitchValue] = useState(false);
  const [supplierModalVisible, setSupplierModalVisible] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [supplierForm] = Form.useForm();

  useEffect(() => {
    loadData();
  }, [activeTab]);

  // Filter mappings based on search text
  useEffect(() => {
    if (searchText.trim() === '') {
      setFilteredMappings(mappings);
    } else {
      const filtered = mappings.filter(mapping => {
        const supplier = suppliers.find(s => s.id === mapping.supplier_id);
        const subcategory = subcategories.find(cat => cat.id === mapping.subcategory_id);
        const searchLower = searchText.toLowerCase();

        return (
          mapping.product_code?.toLowerCase().includes(searchLower) ||
          supplier?.supplier_name?.toLowerCase().includes(searchLower) ||
          supplier?.supplier_code?.toLowerCase().includes(searchLower) ||
          subcategory?.category_name?.toLowerCase().includes(searchLower) ||
          subcategory?.subcategory_name?.toLowerCase().includes(searchLower) ||
          subcategory?.subcategory_code?.toLowerCase().includes(searchLower)
        );
      });
      setFilteredMappings(filtered);
    }
  }, [searchText, mappings, suppliers, subcategories]);

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const loadMasterProducts = async (supplierId = null, subcategoryId = null) => {
    try {
      console.log('Loading master products for supplier:', supplierId, 'subcategory:', subcategoryId);
      const response = await suppliersApi.get(API_ENDPOINTS.MASTER_PRODUCTS({
        supplier_id: supplierId,
        subcategory_id: subcategoryId,
        is_active: true
      }));
      console.log('Master products response:', response.data);
      setMasterProducts(response.data?.data || []);
    } catch (error) {
      console.error('Failed to load master products:', error);
      setMasterProducts([]);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      console.log('=== LOAD DATA DEBUG ===');
      console.log('Loading supplier data for activeTab:', activeTab);

      if (activeTab === 'mappings') {
        // Try regular API first
        try {
          console.log('Fetching mappings...');
          // Use centralized API configuration
          const mappingsResponse = await suppliersApi.get(API_ENDPOINTS.SUPPLIERS_MAPPINGS(100));
          console.log('Mappings response:', mappingsResponse.data);

          if (mappingsResponse.data && mappingsResponse.data.data) {
            const mappingsData = mappingsResponse.data.data.mappings || [];
            console.log('Setting mappings data:', mappingsData);

            // Log each mapping with is_default details
            mappingsData.forEach((mapping, index) => {
              console.log(`Mapping ${index + 1}:`, {
                id: mapping.id,
                product_code: mapping.product_code,
                is_default: mapping.is_default,
                is_default_type: typeof mapping.is_default,
                supplier_name: mapping.supplier_name
              });
            });

            setMappings(mappingsData);
            setFilteredMappings(mappingsData);
          } else {
            console.log('No mapping data found, response:', mappingsResponse.data);
            setMappings([]);
            setFilteredMappings([]);
          }
        } catch (error) {
          console.error('Failed to load mappings:', error);
          message.error('Failed to load mappings data: ' + (error.response?.data?.message || error.message));
          return;
        }
      }

      console.log('Fetching suppliers and subcategories...');
      const [suppliersResponse, subcategoriesResponse] = await Promise.all([
        suppliersApi.get(API_ENDPOINTS.SUPPLIERS()),
        suppliersApi.get(API_ENDPOINTS.SUPPLIERS_SUBCATEGORIES())
      ]);

      console.log('Suppliers response:', suppliersResponse.data);
      console.log('Subcategories response:', subcategoriesResponse.data);

      setSuppliers(suppliersResponse.data?.data || []);
      setSubcategories(subcategoriesResponse.data?.data || []);

      console.log('Data loaded successfully');
      console.log('=== END LOAD DATA DEBUG ===');

    } catch (error) {
      console.error('Failed to load data:', error);
      message.error('Failed to load data: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleAddMapping = () => {
    setEditingMapping(null);
    form.resetFields();
    setSwitchValue(false); // Reset switch state
    setMasterProducts([]);
    setModalVisible(true);
  };

  const handleEditMapping = (record) => {
    console.log('=== EDIT MAPPING DEBUG ===');
    console.log('Record being edited:', record);
    console.log('is_default value:', record.is_default);
    console.log('is_default type:', typeof record.is_default);

    const isDefaultValue = record.is_default === 1 || record.is_default === true || record.is_default === '1';
    console.log('Converted isDefault value:', isDefaultValue);

    // Force set switch value immediately
    setSwitchValue(isDefaultValue);

    setEditingMapping(record);

    // Set form values with longer delay
    setTimeout(() => {
      form.setFieldsValue({
        subcategoryId: record.subcategory_id,
        supplierId: record.supplier_id,
        productCode: record.product_code,
        isDefault: isDefaultValue,
        priority: record.priority
      });

      console.log('Form values after set:', form.getFieldsValue());
      console.log('Switch state:', switchValue);
      console.log('=== END EDIT DEBUG ===');
    }, 100);

    // Load master products for editing
    loadMasterProducts(record.supplier_id, record.subcategory_id);
    setModalVisible(true);
  };

  const handleSubcategoryChange = (subcategoryId) => {
    const supplierId = form.getFieldValue('supplierId');
    if (supplierId && subcategoryId) {
      loadMasterProducts(supplierId, subcategoryId);
      form.setFieldsValue({ productCode: undefined });
    } else {
      setMasterProducts([]);
    }
  };

  const handleSupplierChange = (supplierId) => {
    const subcategoryId = form.getFieldValue('subcategoryId');
    if (supplierId && subcategoryId) {
      loadMasterProducts(supplierId, subcategoryId);
      form.setFieldsValue({ productCode: undefined });
    } else {
      setMasterProducts([]);
    }
  };

  const handleDeleteMapping = async (record) => {
    setLoading(true);
    try {
      console.log('=== DELETE MAPPING DEBUG ===');
      console.log('Deleting mapping:', record);

      await suppliersApi.delete(`${API_ENDPOINTS.SUPPLIERS_MAPPINGS_CREATE()}/${record.id}`);

      message.success('Supplier mapping deleted successfully');

      // Refresh data after deletion
      setTimeout(() => {
        if (activeTab === 'mappings') {
          loadData();
        }
      }, 100);

    } catch (error) {
      console.error('Delete mapping error:', error);
      message.error(error.response?.data?.message || 'Failed to delete mapping');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSupplier = async (record) => {
    setLoading(true);
    try {
      await suppliersApi.delete(`${API_ENDPOINTS.SUPPLIERS()}${record.id}`);
      message.success('Supplier deleted successfully');

      // Refresh suppliers data
      if (activeTab === 'suppliers') {
        loadData();
      }
    } catch (error) {
      console.error('Delete supplier error:', error);
      message.error(error.response?.data?.message || 'Failed to delete supplier');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSupplier = () => {
    setEditingSupplier(null);
    supplierForm.resetFields();
    setSupplierModalVisible(true);
  };

  const handleEditSupplier = (record) => {
    setEditingSupplier(record);
    supplierForm.setFieldsValue({
      supplier_name: record.supplier_name,
      supplier_code: record.supplier_code,
      description: record.description,
      is_active: record.is_active
    });
    setSupplierModalVisible(true);
  };

  const handleSubmitSupplier = async (values) => {
    setLoading(true);
    try {
      if (editingSupplier) {
        await suppliersApi.put(`${API_ENDPOINTS.SUPPLIERS()}${editingSupplier.id}`, values);
        message.success('Supplier updated successfully');
      } else {
        await suppliersApi.post(API_ENDPOINTS.SUPPLIERS(), values);
        message.success('Supplier created successfully');
      }

      setSupplierModalVisible(false);

      // Refresh suppliers data
      if (activeTab === 'suppliers') {
        loadData();
      }
    } catch (error) {
      console.error('Supplier form submission error:', error);
      message.error(error.response?.data?.message || 'Failed to save supplier');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // Get subcategory code from selected subcategory
      const selectedSubcategory = subcategories.find(cat => cat.id === values.subcategoryId);
      const mappingData = {
        subcategoryCode: selectedSubcategory?.subcategory_code,
        supplierId: values.supplierId,
        productCode: values.productCode,
        isDefault: values.isDefault,  // Langsung pakai nilai dari form switch
        priority: values.priority || 1
      };

      // COMPREHENSIVE DEBUG LOGGING
      console.log('=== FORM SUBMISSION DEBUG ===');
      console.log('Raw form values:', values);
      console.log('values.isDefault raw:', values.isDefault);
      console.log('values.isDefault type:', typeof values.isDefault);
      console.log('values.isDefault === true:', values.isDefault === true);
      console.log('values.isDefault === false:', values.isDefault === false);
      console.log('values.isDefault == true:', values.isDefault == true);
      console.log('values.isDefault == false:', values.isDefault == false);
      console.log('!!values.isDefault:', !!values.isDefault);
      console.log('Selected subcategory:', selectedSubcategory);
      console.log('Mapping data being sent:', mappingData);
      console.log('Editing mapping ID:', editingMapping?.id);
      console.log('Final isDefault value type:', typeof mappingData.isDefault);
      console.log('Final isDefault value:', mappingData.isDefault);

      if (editingMapping) {
        console.log('Making PUT request to:', `${API_ENDPOINTS.SUPPLIERS_MAPPINGS_CREATE()}/${editingMapping.id}`);
        console.log('Request payload:', JSON.stringify(mappingData, null, 2));

        const response = await suppliersApi.put(`${API_ENDPOINTS.SUPPLIERS_MAPPINGS_CREATE()}/${editingMapping.id}`, mappingData);
        console.log('PUT response:', response.data);
        message.success('Supplier mapping updated successfully');
      } else {
        console.log('Making POST request to:', API_ENDPOINTS.SUPPLIERS_MAPPINGS_CREATE());
        console.log('Request payload:', JSON.stringify(mappingData, null, 2));

        const response = await suppliersApi.post(API_ENDPOINTS.SUPPLIERS_MAPPINGS_CREATE(), mappingData);
        console.log('POST response:', response.data);
        message.success('Supplier mapping created successfully');
      }

      setModalVisible(false);
      // Force refresh data after a short delay to ensure server has processed the update
      setTimeout(() => {
        if (activeTab === 'mappings') {
          console.log('Refreshing data after form submission...');
          loadData();
        }
      }, 100);
    } catch (error) {
      console.error('Form submission error:', error);
      console.error('Error response:', error.response?.data);
      message.error(error.response?.data?.message || 'Failed to save mapping');
    } finally {
      setLoading(false);
    }
  };

  const mappingColumns = [
    {
      title: 'Category',
      dataIndex: 'subcategory_id',
      key: 'subcategory_id',
      render: (id) => {
        const subcategory = subcategories.find(cat => cat.id === id);
        if (subcategory) {
          return (
            <div>
              <div style={{ fontWeight: 'bold' }}>{subcategory.category_name}</div>
              <div style={{ fontSize: '12px', color: '#666' }}>{subcategory.category_code}</div>
            </div>
          );
        }
        return `ID: ${id}`;
      }
    },
    {
      title: 'Subcategory',
      dataIndex: 'subcategory_id',
      key: 'subcategory_name',
      render: (id) => {
        const subcategory = subcategories.find(cat => cat.id === id);
        if (subcategory) {
          return (
            <div>
              <div style={{ fontWeight: 'bold' }}>{subcategory.subcategory_name}</div>
              <div style={{ fontSize: '12px', color: '#666' }}>{subcategory.subcategory_code}</div>
            </div>
          );
        }
        return `ID: ${id}`;
      }
    },
    {
      title: 'Supplier',
      dataIndex: 'supplier_id',
      key: 'supplier_name',
      render: (id) => {
        const supplier = suppliers.find(s => s.id === id);
        if (supplier) {
          return (
            <div>
              <div style={{ fontWeight: 'bold' }}>{supplier.supplier_name}</div>
              <div style={{ fontSize: '12px', color: '#666' }}>{supplier.supplier_code}</div>
            </div>
          );
        }
        return `ID: ${id}`;
      }
    },
    {
      title: 'Product Code',
      dataIndex: 'product_code',
      key: 'product_code',
      render: (text) => <Tag color="blue">{text}</Tag>
    },
    {
      title: 'Type',
      key: 'type',
      render: (_, record) => {
        const isDefault = record.is_default === 1 || record.is_default === true || record.is_default === '1';
        const hasMerchantId = record.merchant_id && record.merchant_id !== null && record.merchant_id !== 0;

        return (
          <div>
            {isDefault ?
              <Tag color="green">Default</Tag> :
              <Tag color="blue">Standard</Tag>
            }
            {hasMerchantId && <Tag color="orange">Merchant Specific</Tag>}
          </div>
        );
      }
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority) => <Tag color="purple">P{priority}</Tag>
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditMapping(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this supplier mapping?"
            description="This action cannot be undone."
            onConfirm={() => handleDeleteMapping(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="danger"
              size="small"
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const supplierColumns = [
    {
      title: 'Supplier Name',
      dataIndex: 'supplier_name',
      key: 'supplier_name'
    },
    {
      title: 'Supplier Code',
      dataIndex: 'supplier_code',
      key: 'supplier_code',
      render: (code) => <Tag color="blue">{code}</Tag>
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description'
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active) => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditSupplier(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this supplier?"
            description="This action cannot be undone."
            onConfirm={() => handleDeleteSupplier(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="danger"
              size="small"
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card title="Supplier Management" style={{ marginBottom: 16 }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          {false && <TabPane tab="Supplier Mappings" key="mappings">
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddMapping}
              >
                Add Supplier Mapping
              </Button>
              <Input
                placeholder="Search by product code, supplier, or category..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                style={{ width: 300 }}
                allowClear
              />
            </div>
            <Table
              columns={mappingColumns}
              dataSource={filteredMappings}
              rowKey="id"
              loading={loading}
              pagination={false}
            />
          </TabPane>}

          <TabPane tab="Suppliers" key="suppliers">
            <div style={{ marginBottom: 16 }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddSupplier}
              >
                Add Supplier
              </Button>
            </div>
            <Table
              columns={supplierColumns}
              dataSource={suppliers}
              rowKey="id"
              loading={loading}
              pagination={false}
            />
          </TabPane>

          <TabPane tab="Subcategories" key="subcategories">
            <Table
              columns={[
                {
                  title: 'Category',
                  dataIndex: 'category_name',
                  key: 'category_name',
                  render: (text, record) => (
                    <div>
                      <div style={{ fontWeight: 'bold' }}>{text}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>{record.category_code}</div>
                    </div>
                  )
                },
                {
                  title: 'Subcategory',
                  dataIndex: 'subcategory_name',
                  key: 'subcategory_name',
                  render: (text, record) => (
                    <div>
                      <div style={{ fontWeight: 'bold' }}>{text}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>{record.subcategory_code}</div>
                    </div>
                  )
                },
                {
                  title: 'Description',
                  dataIndex: 'description',
                  key: 'description'
                }
              ]}
              dataSource={subcategories}
              rowKey="id"
              loading={loading}
              pagination={false}
            />
          </TabPane>
        </Tabs>
      </Card>

      <Modal
        title={editingMapping ? 'Edit Supplier Mapping' : 'Add Supplier Mapping'}
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="subcategoryId"
            label="Subcategory"
            rules={[{ required: true, message: 'Please select a subcategory' }]}
          >
            <Select
              placeholder="Select subcategory"
              onChange={handleSubcategoryChange}
            >
              {subcategories.map(cat => (
                <Option key={cat.id} value={cat.id}>
                  {cat.category_name} - {cat.subcategory_name} ({cat.subcategory_code})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="supplierId"
            label="Supplier"
            rules={[{ required: true, message: 'Please select a supplier' }]}
          >
            <Select
              placeholder="Select supplier"
              onChange={handleSupplierChange}
            >
              {suppliers.map(supplier => (
                <Option key={supplier.id} value={supplier.id}>
                  {supplier.supplier_name} ({supplier.supplier_code})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="productCode"
            label="Product Code"
            rules={[{ required: true, message: 'Please select product code' }]}
          >
            <Select
              placeholder={masterProducts.length === 0 ? "Select supplier and subcategory first" : "Select product code"}
              disabled={masterProducts.length === 0}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {masterProducts.map(product => (
                <Option key={product.id} value={product.product_code}>
                  {product.product_name} ({product.product_code})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="isDefault"
            valuePropName="checked"
          >
            <Switch
              ref={switchRef}
              checked={switchValue}
              checkedChildren="Default"
              unCheckedChildren="Standard"
              onChange={(checked) => {
                console.log('=== TOGGLE CHANGE DETECTED ===');
                console.log('Toggle switched to:', checked);
                console.log('Toggle value type:', typeof checked);

                // Update local state
                setSwitchValue(checked);

                // Force update form field value
                form.setFieldsValue({ isDefault: checked });

                console.log('Updated form values:', form.getFieldsValue());
                console.log('Switch local state:', switchValue);
                console.log('Editing mapping ID:', editingMapping?.id);
                console.log('=== END TOGGLE LOG ===');
              }}
            /> Set as Default for this Subcategory
          </Form.Item>

          <Form.Item
            name="priority"
            label="Priority"
            rules={[{ required: true, message: 'Please enter priority' }]}
          >
            <InputNumber min={1} max={10} placeholder="1 (highest)" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingMapping ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Supplier CRUD Modal */}
      <Modal
        title={editingSupplier ? 'Edit Supplier' : 'Add Supplier'}
        visible={supplierModalVisible}
        onCancel={() => setSupplierModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form
          form={supplierForm}
          layout="vertical"
          onFinish={handleSubmitSupplier}
        >
          <Form.Item
            name="supplier_name"
            label="Supplier Name"
            rules={[{ required: true, message: 'Please enter supplier name' }]}
          >
            <Input placeholder="Enter supplier name" />
          </Form.Item>

          <Form.Item
            name="supplier_code"
            label="Supplier Code"
            rules={[{ required: true, message: 'Please enter supplier code' }]}
          >
            <Input
              placeholder="Enter supplier code"
              style={{ textTransform: 'uppercase' }}
              disabled={!!editingSupplier}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea placeholder="Enter supplier description" rows={3} />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Status"
            valuePropName="checked"
          >
            <Switch
              checkedChildren="Active"
              unCheckedChildren="Inactive"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setSupplierModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingSupplier ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SupplierManagementSimple;